import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Checkbox,
  Select,
  notification,
  InputNumber,
  Typography,
  Button
} from "antd";

import axiosInstance from "utilities/axios";
import {
  createEstimate,
  setClinicData,
  setCustomerData,
  updateDiscounts,
  updateEstimate,
} from "utilities/redux/slices/currentEstimate";
import { fetchProducts } from "utilities/redux/slices/productsSlice";

const { Title, Text } = Typography;

const CustomerDetails = ({ show, handleClose }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const selectedSubsidy = Form.useWatch("programEligability", form);
  const allvals = Form.useWatch([], form);

  var customerData = useSelector((state) => state.currentEstimate.customerData);
  var clinicData = useSelector((state) => state.currentEstimate.clinicData);
  var currentEstimate = useSelector((state) => state.currentEstimate);
  var currentProducts = useSelector((state) => state.currentEstimate.products);
  var allProducts = useSelector((state) => state.products.data);
  var clinics = useSelector((state) => state.clinics.data);
  var subsidies = useSelector((state) => state.subsidies.data);

  const [affinityPartners, setAffinityPartners] = useState(null);
  const [marketingCodes, setMarketingCodes] = useState(null);
  const [programVisible, setProgramVisible] = useState(false);
  const [numberPrograms, setNumberPrograms] = useState(0);
  const [province, setProvince] = useState(null);
  const [financingOptions, setFinancingOptions] = useState(null);
  const [selectedFinancingOption, setSelectedFinancingOption] = useState(null);

  const [customModalVisible, setCustomModalVisible] = useState(false);
  const [customValue, setCustomValue] = useState(0);
  const [currentCustomDiscount, setCurrentCustomDiscount] = useState(null);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedCustomDiscounts, setSelectedCustomDiscounts] = useState(
    customerData.marketingDiscounts || {}
  );
  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const [isProgramEligible, setIsProgramEligible] = useState(customerData?.programEligability);

  const defaults = {
    name: "",
    email: "",
    programEligability: null,
    programDiscount: null,
    marketingPromotion: null,
    affinityProgram: null,
    financingOption: null,
    downPayment: null,
    clinicNo: "",
    province: "",
  };
  const mergedData = { ...defaults, ...customerData, ...clinicData };

  useEffect(() => {
    dispatch(fetchProducts());

    axiosInstance
      .get("/financing-options", {
        params: {
          active: true,
        },
      })
      .then((response) => {
        setFinancingOptions(response.data);

        if (customerData.financingOption) {
          const option = response.data.find((option) => option.id === customerData.financingOption);
          setSelectedFinancingOption(option);
        }
      })
      .catch((error) => {
        notification.error({
          message: "Error",
          description: error.message,
        });
      });

    axiosInstance
      .get("/discounts", {
        params: {
          types: ["affinity"],
          excludeExpired: true,
        },
      })
      .then((response) => {
        setAffinityPartners(response.data);
      })
      .catch((error) => {
        notification.error({
          message: "Error",
          description: error.message,
        });
      });

    axiosInstance
      .get("/discounts", {
        params: {
          types: ["marketing", "matrix"],
          excludeExpired: true,
        },
      })
      .then((response) => {
        setMarketingCodes(response.data);

        const matrix = response.data.find((code) => code.discountType === "matrix");
        if (matrix && (matrix.id in (customerData.marketingDiscounts || {}) || currentProducts?.length === 0)) {
          setSelectedCustomDiscounts((prev) => ({
            ...prev,
            [matrix.id]: -1,
          }));
        }
      })
      .catch((error) => {
        notification.error({
          message: "Error",
          description: error.message,
        });
      });
  }, [customerData, clinicData]);

  useEffect(() => {
    if (clinicData.province) {
      setProvince(clinicData.province);
      const foundSubsidy = subsidies?.filter((subsidy) => subsidy.province === clinicData.province);
      const isEligible = foundSubsidy !== undefined && foundSubsidy.discountValue !== 0;
      setProgramVisible(isEligible);
      setIsProgramEligible(customerData.programEligability);
      setNumberPrograms(foundSubsidy?.length);
    }
  }, [clinicData, customerData]);

  useEffect(() => {
    form.setFieldsValue(customerData);
    setSelectedCustomDiscounts(customerData.marketingDiscounts || {});
  }, [form, customerData]);

  useEffect(() => {
    let tempClinic = null;
    let tempProvince = null;
    if (clinics?.length > 0 && !clinicData.clinicNo) {
      tempClinic = clinics[0].clinicNo;
      tempProvince = clinics[0].province;
    } else if (clinicData.clinicNo) {
      tempClinic = clinicData.clinicNo;
      tempProvince = clinicData.province;
    } else {
      tempClinic = null;
      tempProvince = null;
    }

    form.setFieldValue("clinicNo", tempClinic);
    form.setFieldValue("province", tempProvince);
    setProvince(tempProvince);
    setProgramVisible(subsidies?.some((subsidy) => subsidy.province === tempProvince));
    const ontarioSubsidy = subsidies?.find((subsidy) => subsidy["province"] === "ON");
    const otherSubsidy = subsidies?.find((subsidy) => subsidy["id"] === customerData.programEligability && subsidy.province === tempProvince);
    form.setFieldValue("programEligability", ontarioSubsidy && tempProvince === "ON" ? ontarioSubsidy["id"] : otherSubsidy ? otherSubsidy["id"] : null );
    setIsProgramEligible(ontarioSubsidy && tempProvince === "ON" ? true : otherSubsidy ? otherSubsidy["id"] : false);
  }, [form, clinicData.clinicNo, clinics]);

  useEffect(() => {
    setCurrentProduct(null);
    setCustomValue(null);

    if (currentCustomDiscount === null || currentCustomDiscount === undefined) {
      return;
    }

    if (currentCustomDiscount !== null && marketingCodes?.find((code) => code.id === currentCustomDiscount)?.upTo === false) {
      return;
    }

    axiosInstance
      .get(`/discounts/${currentCustomDiscount}/custom`, {
        params: {
          estimateId: currentEstimate.id,
          province: province,
          clinicNo: clinicData.clinicNo,
        },
      })
      .then((response) => {
        setProducts(response.data);
      })
      .catch((error) => {
        notification.error({
          message: "Error",
          description: error.message,
        });
      });
  }, [currentCustomDiscount, currentEstimate, province, clinicData]);

  useEffect(() => {
    if (!customerData.financingOption) {
      form.setFieldValue("downPayment", null);
      setSelectedFinancingOption(null);
    }
  }, [customerData.financingOption]);

  const handleValuesChange = (changedValues, allValues) => {
    if (changedValues.hasOwnProperty("programEligability")) {
      setIsProgramEligible(changedValues.programEligability);
    }
  };

  const handleClinicChange = (value) => {
    const selectedClinic = clinics?.find((clinic) => clinic.clinicNo === value);
    if (selectedClinic) {
      form.setFieldsValue({
        province: selectedClinic.province,
      });
      dispatch(setClinicData({
        clinicNo: selectedClinic.clinicNo,
        province: selectedClinic.province,
      }));
      setProvince(selectedClinic.province);
      const foundSubsidy = subsidies?.filter((subsidy) => subsidy.province === selectedClinic.province);
      const isEligible = foundSubsidy !== undefined && foundSubsidy.discountValue !== 0;
      setProgramVisible(isEligible);
      setNumberPrograms(foundSubsidy?.length);

      const ontarioSubsidy = subsidies?.find((subsidy) => subsidy.province === "ON");
      if (selectedClinic.province === "ON" && ontarioSubsidy) {
        form.setFieldValue("programEligability", ontarioSubsidy["id"]);
      } else {
        form.setFieldValue("programEligability", null);
      }
    }
  };

  const handleCustomDiscountOk = () => {
    if (currentCustomDiscount && marketingCodes?.find((code) => code.id === currentCustomDiscount)?.upTo) {
      setSelectedCustomDiscounts((prev) => ({
        ...prev,
        [currentCustomDiscount]: {
          ...prev[currentCustomDiscount],
          [currentProduct.id]: customValue
        },
      }));
    } else {
      if (currentEstimate.products?.length === 0) {
        setSelectedCustomDiscounts((prev) => ({
          ...prev,
          [currentCustomDiscount]: -1,
        }));
      };

      for (let i = 0; i < currentEstimate.products?.length; i++) {
        setSelectedCustomDiscounts((prev) => ({
          ...prev,
          [currentCustomDiscount]: -1,
        }));
      }
    }

    setCustomValue(0);
    setCurrentCustomDiscount(null);
    setCustomModalVisible(false);
    setCurrentProduct(null);
  };

  useEffect(() => {
    const newPromotion = [];
    Object.entries(selectedCustomDiscounts).forEach(([discountId, products]) => {
      if (products === -1) {
        newPromotion.push(`${discountId}-all`);
        return;
      }

      const productIds = Object.keys(products);

      productIds.forEach(productId => {
        newPromotion.push(`${discountId}-${productId}`);
      });
    });
    form.setFieldValue("marketingPromotion", newPromotion);
  }, [selectedCustomDiscounts, currentEstimate.products?.length, form]);

  const handleCustomDiscountCancel = () => {
    setCustomValue(0);
    setCurrentCustomDiscount(null);
    setCustomModalVisible(false);
    setCurrentProduct(null);
    
    setSelectedCustomDiscounts({ ...selectedCustomDiscounts });
  };

  const renderDiscountLabel = (key) => {
    const [codeId, productId] = key.split("-");

    const code = marketingCodes?.find((c) => c.id === parseInt(codeId));

    if (code && productId === "all") {
      return `${code.discountName} - All Products`;
    } else {
      const product = allProducts?.find((p) => p.id === parseInt(productId));
      return code && product
        ? `${code.discountName} - ${product.name}`
        : key;
    }
  };

  return (
    <Modal
      open={show}
      onCancel={() => {
        form.submit();
        handleClose();
      }}
      onOk={form.submit}
      title="Clinic Details"
      destroyOnClose
      footer={
        <Button
          type="primary"
          onClick={() => {
            form.submit();
          }}
        >
          Save
        </Button>
      }
      zIndex={1000}
      width={800}
    >
      <Form
        form={form}
        initialValues={mergedData}
        onFinish={(values) => {      
          if (currentEstimate.id) {
            if (customerData.name && customerData.name !== '' && values.name !== customerData.name) {
              setOpenConfirmModal(true);
            } else {
              values.marketingDiscounts = selectedCustomDiscounts;
              dispatch(setClinicData(values));
              dispatch(setCustomerData(values));
              dispatch(updateEstimate());
              dispatch(updateDiscounts(values));
            }
          } else {
            dispatch(createEstimate());
          }

          if (!customerData.name || customerData.name === '' || values.name === customerData.name) {
            handleClose();
          }
        }}
        onValuesChange={handleValuesChange}
      >
        <Form.Item
          label="Home Clinic"
          name="clinicNo"
          rules={[
            {
              required: true,
              message: "Please select a clinic",
            },
          ]}
        >
          <Select
            placeholder="Select a clinic"
            showSearch
            filterOption={(input, option) =>
              option.label.toLowerCase().includes(input.toLowerCase())
            }
            options={clinics?.map((clinic) => ({
              value: clinic.clinicNo,
              label: `${clinic.clinicNo} - ${clinic.clinicName}`,
            }))}
            onChange={handleClinicChange}
          />
        </Form.Item>
        <Form.Item label="Province" name="province" hidden>
          <Input disabled />
        </Form.Item>
        <Title level={5}>Customer Details</Title>
        <Form.Item
          label="Client First Name"
          name="name"
        >
          <Input placeholder="Name" />
        </Form.Item>
        {/* <Form.Item
          label="Email"
          name="email"
          rules={[
            {
              type: "email",
              message: "The input is not valid E-mail",
            },
            {
              required: true,
              message: "Please input the client's email",
            },
          ]}
        >
          <Input placeholder="Email" />
        </Form.Item> */}

        {numberPrograms == 1 ? (
          <Form.Item
            label={`${subsidies?.find((subsidy) => subsidy["province"] === province)?.discountName} Program Eligibility`}
            name="programEligability"
          >
            <Checkbox
              checked={typeof form.getFieldValue("programEligability") === "number"}
              onChange={(e) => {
                if (e.target.checked) {
                  const subsidy = subsidies?.find((subsidy) => subsidy["province"] === province);
                  setIsProgramEligible(true);
                  form.setFieldValue("programEligability", subsidy?.id || null);
                } else {
                  setIsProgramEligible(null);
                  form.setFieldValue("programEligability", null);
                }
              }}
            />
          </Form.Item>
        ) : numberPrograms > 1 ? (
          <Form.Item
            label="Select Subsidy Program"
            name="programEligability"
          >
            <Select
              placeholder="Select Subsidy Program"
              allowClear
              showSearch
              options={subsidies?.filter((subsidy) => subsidy["province"] === province).map((subsidy) => ({
                label: subsidy.discountName,
                value: subsidy.id,
              }))}
            />
          </Form.Item>
        ) : null}
        {(() => {
          const foundSubsidy = typeof selectedSubsidy === 'number'
            ? subsidies?.find((subsidy) => subsidy["id"] === selectedSubsidy)
            : subsidies?.find((subsidy) => subsidy["province"] === province);

          if (isProgramEligible && foundSubsidy?.upTo) {
            return (
              <Form.Item
                label={`${foundSubsidy?.discountName} Amount (per Aid)`}
                name="programDiscount"
                rules={[
                  {
                    required: isProgramEligible,
                    message: `${foundSubsidy?.discountName} Amount (per Aid)`,
                  },
                  {
                    type: "number",
                    max: foundSubsidy?.discountValue,
                    message: `Maximum discount amount is $${foundSubsidy?.discountValue}`,
                  }
                ]}
              >
                <InputNumber
                  placeholder={`${foundSubsidy?.discountName} Amount`}
                  prefix="$"
                  min={1}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            );
          }
          return null;
        })()}
        <Form.Item label="Discount Reason" name="marketingPromotion">
          <Select
            placeholder="Select Discount Reason"
            allowClear
            showSearch
            mode="multiple"
            open={false}
            value={form.getFieldValue("marketingPromotion")}
            options={form.getFieldValue("marketingPromotion")?.map((key) => ({
              label: renderDiscountLabel(key),
              value: key,
            })) || []}
            style={{ width: "100%" }}
            onClick={() => {
              setCustomModalVisible(true);
            }}
            onDeselect={(value) => {
              const [discountId, productId] = value.split("-");
              const newDiscounts = { ...selectedCustomDiscounts };

              if (newDiscounts[discountId]) {
                if (productId === "all") {
                  delete newDiscounts[discountId];
                } else {
                  const updatedProducts = { ...newDiscounts[discountId] };
                  delete updatedProducts[productId];

                  if (Object.keys(updatedProducts)?.length === 0) {
                    delete newDiscounts[discountId];
                  } else {
                    newDiscounts[discountId] = updatedProducts;
                  }
                }
              }

              setSelectedCustomDiscounts({ ...newDiscounts });
            }}
          />
        </Form.Item>
        <Form.Item label="Affinity Program" name="affinityProgram">
          <Select
            placeholder="Select Affinity Partner"
            allowClear
            showSearch
            filterOption={(input, option) =>
              option.label.toLowerCase().includes(input.toLowerCase())
            }
            options={affinityPartners?.map((partner) => ({
              label: partner.discountName,
              value: partner.id,
            }))}
            style={{ width: "100%" }}
          />
        </Form.Item>
        <Form.Item label="Financing Option" name="financingOption">
          <Select
            placeholder="Select Financing Option"
            allowClear
            showSearch
            options={[
              ...(financingOptions ? financingOptions.map((option) => ({
                label: option.name,
                value: option.id,
              })) : []),
              { label: "None", value: null }
            ]}
            style={{ width: "100%" }}
            onChange={(value) => {
              if (value === null) {
                setSelectedFinancingOption(null);
                form.setFieldValue("downPayment", null);
                return;
              }
              const option = financingOptions.find((option) => option.id === value);
              setSelectedFinancingOption(option);
              form.setFieldValue("downPayment", option?.down_payment || null);
            }}
          />
        </Form.Item>
        <Form.Item
          label="Down Payment"
          name="downPayment"
          hidden={!selectedFinancingOption}
          rules={[
            {
              required: !!selectedFinancingOption,
              message: "Please input the down payment",
            }
          ]}
        >
          <InputNumber
            placeholder={`Down Payment (minimum of ${selectedFinancingOption ? selectedFinancingOption.down_payment : 0}%)`}
            suffix="%"
            min={selectedFinancingOption ? selectedFinancingOption.down_payment : 0}
            max={100}
            style={{ width: "100%" }}
            onChange={(value) => {
              if (value === null || value === undefined) {
                form.setFieldValue("downPayment", selectedFinancingOption ? selectedFinancingOption.down_payment : 0);
              }
            }}
          />
        </Form.Item>
      </Form>
      <Modal
        title="Discounts"
        open={customModalVisible}
        zIndex={2000}
        onCancel={handleCustomDiscountCancel}
        footer={[
          <Button key="cancel" onClick={handleCustomDiscountCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleCustomDiscountOk}
            disabled={marketingCodes?.find((code) => code.id === currentCustomDiscount)?.upTo ? !currentProduct || !currentCustomDiscount || customValue <= 0 : !currentCustomDiscount}
          >
            Save
          </Button>
        ]}
      >
        <Text>Select Discount</Text>
        <Select
          placeholder="Select Discount Code"
          style={{ width: "100%", marginBottom: "16px", marginTop: "8px" }}
          options={marketingCodes?.filter(
            (code) => {
              const discounts = selectedCustomDiscounts[code.id] || {};
              const productIds = Object.keys(discounts);
              if (
                productIds?.length > 0 &&
                productIds?.length === currentEstimate.products?.length &&
                productIds.every((pid) => discounts[pid] === -1)
              ) {
                return false;
              }
              return true;
            }
          ).map((code) => ({
            label: code.discountName,
            value: code.id,
            disabled: code.upTo && currentEstimate.products?.length === 0,
          }))}
          onChange={(value) => {
            setCurrentCustomDiscount(value);
          }}
          value={currentCustomDiscount}
        />
        {marketingCodes?.find((code) => code.id === currentCustomDiscount)?.upTo && (
          <>
            <Text>Select Product</Text>
            <Select
              placeholder="Select Product"
              value={currentProduct?.id}
              style={{ width: "100%", marginBottom: "16px", marginTop: "8px" }}
              options={
                products
                  .filter((product) => {
                    const discountsForCurrent = selectedCustomDiscounts[currentCustomDiscount] || {};
                    return !(product.id in discountsForCurrent);
                  })
                  .map((product) => ({
                    label: `${product.name} - ${product.techLevel}`,
                    value: product.id,
                  }))
              }
              onChange={(value) =>
                setCurrentProduct(products?.find((product) => product.id === value))
              }
            />
            <Text>{marketingCodes?.find((code) => code.id === currentCustomDiscount)?.discountName === "Price Match" ? 'Enter New Product Price (Enter Competitor Price per Aid)' : 'Enter Discount Amount (per Aid)'}</Text>
            <InputNumber
              value={customValue !== 0 ? customValue : null}
              onChange={(value) => setCustomValue(value)}
              placeholder={marketingCodes?.find((code) => code.id === currentCustomDiscount)?.discountName === "Price Match" ? 'Enter product price (per aid)' : 'Enter discount amount (per aid)'}
              style={{ width: "100%", marginBottom: "16px", marginTop: "8px" }}
              min={0}
              max={currentProduct?.discountValue}
              prefix="$"
              disabled={!currentProduct || !currentProduct?.upTo}
            />
          </>
        )}
      </Modal>
      <Modal
        title="Confirm Client Name Change"
        open={openConfirmModal}
        onCancel={() => setOpenConfirmModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setOpenConfirmModal(false)}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            onClick={() => {
              allvals.marketingDiscounts = selectedCustomDiscounts;
              dispatch(setClinicData(allvals));
              dispatch(setCustomerData(allvals));
              dispatch(updateEstimate()).then(() => {
                dispatch(updateDiscounts(allvals));
              });
              setOpenConfirmModal(false);
              handleClose();
            }}
          >
            Confirm
          </Button>
        ]}
      >
        <Text>Changing the client name will create a new estimate with the same products and discounts. Are you sure you want to proceed?
        </Text>
      </Modal>
    </Modal>
  );
};

export default CustomerDetails;
